import { DatePipe } from '@angular/common';
import { Component, Input, type OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { LocaleService } from 'src/app/services/locale.service';
import {
  type QaiBadgeKind,
  type QaiIconKind,
  type QaiAssignmentCardKind,
  type QaiAssignmentCardRibbonKind,
} from '@qai/ui';
import { AppConstants } from 'src/app/app-constants';
import { IAssignmentStatus } from 'src/app/interfaces/iassignment-status';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
@Component({
  providers: [DatePipe],
  selector: 'app-assignment-card',
  standalone: false,
  styles: [
    `
      :host {
        display: contents;
      }
      .view-action-disabled {
        pointer-events: none;
        cursor: not-allowed;
        background: var(--button-primary-bg-disabled, var(--bg-disabled)) !important;
        border-color: var(--button-primary-border-disabled, var(--border-secondary)) !important;
        color: var(--button-primary-text-disabled, var(--fg-disabled)) !important;
      }
    `,
  ],
  templateUrl: './assignment-card.html',
})
export class AssignmentCardComponent implements OnInit {
  @Input({ required: true }) assignment!: IAssignmentStatus;

  protected title: string = '';
  protected kind!: ReturnType<typeof this.getKindInfo>;
  protected assignmentUrl: string | undefined;
  protected ariaLabel: string | undefined;
  protected formattedDueDate: string | undefined;
  protected dueDateInfo: ReturnType<typeof this.getDueDateInfo>;
  protected statusInfo!: ReturnType<typeof this.getStatusInfo>;
  protected ribbonIcon: QaiIconKind | undefined;
  protected ribbonKind: QaiAssignmentCardRibbonKind | undefined;
  protected routeCommands: any[] = ['/assignment'];

  constructor(
    private router: Router,
    private amplitudeService: AmplitudeService,
    private datePipe: DatePipe,
    private translocoService: TranslocoService,
    private localeService: LocaleService
  ) {}

  ngOnInit() {
    this.title = this.getAssignmentTitle(this.assignment);
    this.kind = this.getKindInfo(
      this.assignment.moduleAssignmentDTO.moduleAssignmentType
    );
    this.assignmentUrl = this.getAssignmentUrl(this.assignment);
    this.ariaLabel = this.getButtonAriaLabel(
      this.assignment.moduleAssignmentDTO.name
    );
    this.formattedDueDate = this.formatDueDate(
      this.assignment.moduleAssignmentDTO.endDate
    );
    this.dueDateInfo = this.getDueDateInfo(this.assignment);
    this.statusInfo = this.getStatusInfo(
      this.assignment.moduleAssignmentProgressStatus
    );

    // Set ribbon for PATHWAY assignments
    if (this.assignment.moduleAssignmentDTO.moduleAssignmentType === 'PATHWAY') {
      this.ribbonIcon = 'path';
      this.ribbonKind = 'primary';
    }
  }

  protected get isPendingAssessment(): boolean {
    return !!this.assignment?.pathwayInfo?.hasNotStartedAssessment;
  }

  protected get ctaLabelKey(): string {
    if (this.kind === 'pathway' && this.isPendingAssessment) {
      return 'dashboard.START_PATHWAY';
    }
    if (
      this.kind === 'pathway' &&
      this.assignment?.moduleAssignmentProgressStatus ===
        AppConstants.NOT_STARTED_ASSIGNMENT_STATUS
    ) {
      return 'dashboard.GET_STARTED';
    }
    if (this.kind === 'pathway') {
      return 'dashboard.VIEW_PATHWAY';
    }
    return 'dashboard.VIEW_ASSIGNMENT';
  }

  private getAssignmentTitle(assignment: IAssignmentStatus): string {
    return assignment.moduleAssignmentDTO.moduleAssignmentType === AppConstants.TYPE_PATHWAY
      ? assignment.pathwayInfo?.pathwayName || assignment.moduleAssignmentDTO.name
      : assignment.moduleAssignmentDTO.name;
  }

  private calculateDateDifference(date1: Date, date2: Date): number {
    const diffTime = date2.getTime() - date1.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  private getAvailableInLabel(days: number): string {
    return `${this.translocoService.translate('AVAILABLE_IN', {}, 'dashboard')} ${days} ${this.translocoService.translate(days === 1 ? 'DAY' : 'DAYS', {}, 'dashboard')}`;
  }

  private getPastDueLabel(days: number): string {
    return `${days} ${this.translocoService.translate(days === 1 ? 'DAY' : 'DAYS', {}, 'dashboard')} ${this.translocoService.translate('PAST_DUE', {}, 'dashboard')}`;
  }

  private getUntilDueLabel(days: number): string {
    return `${days} ${this.translocoService.translate(days === 1 ? 'DAY' : 'DAYS', {}, 'dashboard')} ${this.translocoService.translate('UNTILL_DUE', {}, 'dashboard')}`;
  }

  private getDueTodayLabel(): string {
    return this.translocoService.translate('DUE_TODAY', {}, 'dashboard');
  }

  private isAssignmentAvailable(assignment: IAssignmentStatus): boolean {
    const currentDate = new Date();
    const startDate = new Date(assignment.moduleAssignmentDTO.startDate);

    return currentDate > startDate;
  }

  /** True when the view button is shown but assignment is not yet available (e.g. STAGED with future start date). */
  protected get isViewActionDisabled(): boolean {
    return !!(
      this.assignmentUrl &&
      !this.isAssignmentAvailable(this.assignment)
    );
  }

  private getAssignmentUrl(assignment: IAssignmentStatus) {
    // For STAGED assignments, show URL even if not yet available
    if (this.isStagedAssignment(assignment.moduleAssignmentDTO.status)) {
      if (!assignment.moduleAssignmentDTO.uuid) {
        return '';
      }
    } else {
      // For non-STAGED assignments, check availability and UUID
      if (!this.isAssignmentAvailable(assignment) || !assignment.moduleAssignmentDTO.uuid) {
        return '';
      }
    }

    const isPathway = assignment.moduleAssignmentDTO.moduleAssignmentType === 'PATHWAY';

    if (isPathway && this.isPendingAssessment) {
      this.routeCommands = ['/assessment-pre-call'];
    } else {
      this.routeCommands = [isPathway ? '/learning-pathway' : '/assignment'];
    }

    return this.router
      .createUrlTree(this.routeCommands, {
        queryParams: this.getQueryParams(),
      })
      .toString();
  }

  private formatDueDate(value: string | undefined): string | undefined {
    if (!value) return undefined;
    return this.localeService.formatDate(value);
  }

  private getButtonAriaLabel(title: string) {
    return `${this.translocoService.translate(
        'VIEW_ASSIGNMENT_FOR',
        {},
        'dashboard'
      )} ${title}`;
  }

  private getKindInfo(value: string) {
    const mapping: Record<string, QaiAssignmentCardKind> = {
      CERTIFICATION: 'certification',
      PRACTICE: 'practice',
      PATHWAY: 'pathway',
    };
    return mapping[value];
  }

  private getStatusInfo(
    value: string,
  ): { label: string; kind: QaiBadgeKind } {
    // Closed takes precedence regardless of top-level status
    // SAFE: internal status field vs fixed AppConstants
    if ((this.assignment.moduleAssignmentDTO.status || '').toLowerCase() === AppConstants.CLOSED_ASSIGNMENT_STATUS) {
      return {
        label: `${this.translocoService.translate('dashboard.CLOSED')}`,
        kind: 'gray-blue-outline' as QaiBadgeKind,
      };
    }
    // Handle STAGED status specifically
    // SAFE: internal status field vs fixed AppConstants
    if (this.assignment.moduleAssignmentDTO.status?.toLowerCase() === AppConstants.STAGED_ASSIGNMENT_STATUS) {
      return {
        label: this.translocoService.translate('UPCOMING', {}, 'dashboard'),
        kind: 'gray' as QaiBadgeKind,
      };
    }

    if (
      this.isTestAssignment()
    ) {
      return {
        label: this.translocoService.translate('TEST_ASSIGNMENT', {}, 'dashboard'),
        kind: 'purple' as QaiBadgeKind,
      };
    }

    const mapping: Record<string, { label: string; kind: QaiBadgeKind }> = {
      COMPLETED: {
        label: this.isCertificationAssignment()
          ? this.translocoService.translate('CERTIFIED', {}, 'dashboard')
          : this.translocoService.translate('COMPLETED', {}, 'dashboard'),
        kind: 'green',
      },
      [AppConstants.NOT_STARTED_ASSIGNMENT_STATUS]: {
        label: this.translocoService.translate('NOT_STARTED', {}, 'dashboard'),
        kind: 'gray',
      },
      [AppConstants.IN_PROGRESS_ASSIGNMENT_STATUS]: {
        label: this.translocoService.translate('IN_PROGRESS', {}, 'dashboard'),
        kind: 'mist-blue',
      },
      [AppConstants.NOT_CERTIFIED_ASSIGNMENT_STATUS]: {
        label: this.translocoService.translate('NOT_CERTIFIED', {}, 'dashboard'),
        kind: 'red-dark',
      },
      INCOMPLETE: {
        label: `${this.translocoService.translate(
          'IN_COMPLETE',
          {},
          'dashboard'
        )}`,
        kind: 'gray',
      },
    };
    return mapping[value] || mapping['INCOMPLETE'];
  }

  private getDueDateInfo(assignment: IAssignmentStatus):
    | {
        label: string;
        kind: QaiBadgeKind;
        icon: QaiIconKind;
      }
    | undefined {
    const {
      startDate,
      endDate,
      status,
      moduleAssignmentType: assignmentType,
    } = assignment.moduleAssignmentDTO;

    // Hide due/availability badge when assignment is CLOSED
    // SAFE: internal status field vs fixed AppConstants
    if ((status || '').toLowerCase() === AppConstants.CLOSED_ASSIGNMENT_STATUS ||
        // SAFE: internal status field vs fixed AppConstants
        (assignment.moduleAssignmentProgressStatus || '').toLowerCase() === AppConstants.CLOSED_ASSIGNMENT_STATUS) {
      return undefined;
    }

    // Handle STAGED status specifically - show "Available in X day(s)" badge
    if (this.isStagedAssignment(status)) {
      const currentDate = new Date();
      const startDateObj = new Date(startDate);
      const daysUntilStart = this.calculateDateDifference(currentDate, startDateObj);
      
      return {
        label: this.getAvailableInLabel(daysUntilStart),
        kind: 'gray',
        icon: 'alert-circle',
      };
    }

    const statusWithoutDueDate = ['COMPLETED', 'CERTIFIED', 'IN_COMPLETE', 'CLOSED'];

    if (statusWithoutDueDate.includes(assignment.moduleAssignmentProgressStatus)) {
      return undefined;
    }

    if (
      this.isTestAssignment() &&
      status &&
      !this.isStagedAssignment(status)
    ) {
      return undefined;
    }

    if ((!endDate || !startDate)) {
      return undefined;
    }

    const currentDate = new Date();
    const startDateObj = new Date(startDate);
    const dueDateObj = new Date(endDate);

    const daysUntilStart = this.calculateDateDifference(
      currentDate,
      startDateObj
    );
    const daysUntilDue = this.calculateDateDifference(currentDate, dueDateObj);

    if (daysUntilStart > 0) {
      return {
        label: this.getAvailableInLabel(daysUntilStart),
        kind: 'gray',
        icon: 'alert-circle',
      };
    }

    if (daysUntilDue < 0) {
      return {
        label: this.getPastDueLabel(Math.abs(daysUntilDue)),
        kind: 'red',
        icon: 'alert-triangle',
      };
    }

    if (daysUntilDue === 0) {
      return { label: this.getDueTodayLabel(), kind: 'yellow', icon: 'alert-circle' };
    }

    if (daysUntilDue <= 7) {
      return {
        label: this.getUntilDueLabel(daysUntilDue),
        kind: 'yellow',
        icon: 'alert-circle',
      };
    }

   if (daysUntilDue <=30){
      return {
        label: this.getUntilDueLabel(daysUntilDue),
        kind: 'gray',
        icon: 'alert-circle',
      };
    }

    return undefined;
  }

  protected getQueryParams(): any {
    const queryParams: any = {
      moduleAssignmentUuid: this.assignment.moduleAssignmentDTO.uuid,
      practiceType: this.assignment.moduleAssignmentDTO.moduleAssignmentType,
    };

    if (this.isPendingAssessment && this.assignment.pathwayInfo?.enrollmentUuid) {
      queryParams['enrollmentUuid'] = this.assignment.pathwayInfo.enrollmentUuid;
    }

    return queryParams;
  }

  isTestAssignment(): boolean {
    return this.assignment.moduleAssignmentDTO?.status === AppConstants.AUTHOR_TESTING_ASSIGNMENT_STATUS_UPPER
  }

  isCertificationAssignment(): boolean {
    return this.assignment.moduleAssignmentDTO.moduleAssignmentType === AppConstants.TYPE_CERTIFICATION;
  }

  isStagedAssignment(status?: string): boolean {
    if (status) {
      // SAFE: internal status field vs fixed AppConstants
      return status.toLowerCase() === AppConstants.STAGED_ASSIGNMENT_STATUS;
    }
    // SAFE: internal status field vs fixed AppConstants
    return this.assignment.moduleAssignmentDTO.status?.toLowerCase() === AppConstants.STAGED_ASSIGNMENT_STATUS;
  }

  protected sendAmplitudeClickViewAssignmentEvent(
    assignment: IAssignmentStatus
  ): void {
    // Sending the Event of Clicking on 'View Assignment' to Amplitude
    this.amplitudeService.trackEvent('assignmentsdashboard_click_assignment', {
      assignment_status: assignment?.moduleAssignmentProgressStatus,
      assignment_type: assignment?.moduleAssignmentDTO?.moduleAssignmentType,
      assignment_duedate: assignment?.moduleAssignmentDTO?.endDate,
    });
  }

  protected onViewActionClick(event: Event): void {
    if (this.isViewActionDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.sendAmplitudeClickViewAssignmentEvent(this.assignment);
  }
}
